import { useState } from 'react';
import { t } from '@aquabox/shared';
import { api, ApiError } from '../api/client';
import { Icon } from './Icon';

/**
 * Yazdırma PDF'i düğmesi (K2c). Makbuz modalındaki `ReceiptPdfButton` deseninin aynısı:
 * düz `<a href>` token ve sürüm başlıklarını taşımaz, bu yüzden PDF `fetch` ile alınır.
 *
 * Pop-up engeli: sekme TIKLAMA ANINDA, eşzamanlı olarak boş açılır (`window.open('')`); PDF
 * gelince o sekmenin adresi atanır. Tarayıcı engellerse sekme hiç açılmaz, hata gösterilir ve
 * `onOpened` ÇAĞRILMAZ — fiş açılmadan "basıldı" işaretlenmez.
 *
 * `onOpened` yalnız PDF gerçekten sekmeye verildikten sonra çağrılır (basıldı işareti).
 *
 * Hata mesajı politikası: `getBlobUrl` `ApiError.message` fırlatır (sunucudan gelen Türkçe
 * metin — 2000 satır sınırı, geçersiz tarih aralığı vb.); bu mesaj doğrudan kullanıcıya
 * gösterilir. `ApiError` dışında bir hata olursa genel `panel.print.error` metnine düşülür —
 * kullanıcı "durun" değil somut bir neden görür.
 */
export function PrintPdfButton({
  path,
  label,
  disabled = false,
  className = 'btn',
  onOpened,
}: {
  path: string;
  label: string;
  disabled?: boolean;
  className?: string;
  onOpened?: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function open(): Promise<void> {
    setErrorMessage(null);
    // Eşzamanlı kısım: async fonksiyonun ilk await'inden ÖNCE çalışır, yani tıklama sırasında.
    const win = window.open('', '_blank');
    if (!win) {
      setErrorMessage(t('panel.print.error'));
      return;
    }
    setBusy(true);
    try {
      const url = await api.getBlobUrl(path);
      win.opener = null;
      win.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      onOpened?.();
    } catch (err) {
      win.close();
      const msg = err instanceof ApiError ? err.message : t('panel.print.error');
      setErrorMessage(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <button
        type="button"
        className={className}
        disabled={busy || disabled}
        onClick={() => {
          void open();
        }}
      >
        <Icon name="print" size={14} /> {label}
      </button>
      {errorMessage ? (
        <span role="alert" style={{ fontSize: 12, color: 'var(--danger)' }}>
          {errorMessage}
        </span>
      ) : null}
    </span>
  );
}
